import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';

export function LanguageSwitcher({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language?.split('-')[0]) || SUPPORTED_LANGUAGES[0];

  const change = (code: string) => {
    // Persistir preferencia en backend si hay user
    try {
      const raw = localStorage.getItem('funcomunion_user');
      if (raw) {
        const u = JSON.parse(raw);
        const uid = u?.uid || u?.id;
        if (uid) {
          fetch(`/api/users/${uid}/lang`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lang: code }),
          }).catch(() => {});
          u.lang = code;
          localStorage.setItem('funcomunion_user', JSON.stringify(u));
        }
      }
    } catch {}
    setOpen(false);

    // Persistir el idioma elegido en localStorage ANTES de navegar,
    // para que la próxima carga lo respete sin volver al cacheado anterior.
    try { localStorage.setItem('funcomunion_lang', code); } catch {}
    // Aplicar inmediatamente al i18n actual (en caso de que la navegación falle)
    i18n.changeLanguage(code).catch(() => {});

    // Navegar a la URL path-based correspondiente para que el HTML inicial
    // sirva con meta tags i18n correctos (importante SEO).
    // /  /en/  /it/  /pt/  /ro/
    const currentPath = window.location.pathname;
    // Quitar prefijo de idioma actual si lo hay
    let rest = currentPath;
    for (const l of SUPPORTED_LANGUAGES) {
      if (l.code !== 'es' && (currentPath === `/${l.code}` || currentPath.startsWith(`/${l.code}/`))) {
        rest = currentPath.slice(`/${l.code}`.length) || '/';
        break;
      }
    }
    if (!rest.startsWith('/')) rest = '/' + rest;
    const target = code === 'es' ? rest : `/${code}${rest === '/' ? '/' : rest}`;
    window.location.href = target + window.location.search + window.location.hash;
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-black/5 text-sm"
        aria-label={`Idioma actual: ${current.label}. Cambiar idioma`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        {!compact && <span><span aria-hidden="true">{current.flag} </span>{current.label}</span>}
        {compact && <span className="text-base" aria-hidden="true">{current.flag}</span>}
        <ChevronDown className="w-3 h-3 opacity-60" aria-hidden="true" />
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px] py-1"
          role="listbox"
          aria-label="Seleccionar idioma"
        >
          {SUPPORTED_LANGUAGES.map(l => (
            <li key={l.code} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={l.code === current.code}
                lang={l.code}
                onClick={() => change(l.code)}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${l.code === current.code ? 'font-semibold text-[#1E3A5F]' : 'text-gray-700'}`}
              >
                <span className="text-base" aria-hidden="true">{l.flag}</span>
                <span>{l.label}</span>
                {l.code === current.code && <span className="ml-auto text-emerald-600" aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
